// ============================================
// BillingDashboard Component
// ============================================
// Main billing overview showing subscription status,
// usage, and links to manage billing
// ============================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BillingDashboard.css';

const BillingDashboard = ({ tenantId }) => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, [tenantId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subscription, usage, and cost in parallel
      const [subRes, usageRes, costRes] = await Promise.all([
        axios.get(`/api/billing/subscription/${tenantId}`),
        axios.get(`/api/billing/usage/${tenantId}`),
        axios.get(`/api/billing/usage/${tenantId}/cost`)
      ]);

      setSubscription(subRes.data.subscription);
      setUsage(usageRes.data.usage);
      setCost(costRes.data.cost);
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      const res = await axios.get(`/api/billing/portal/${tenantId}`);
      if (res.data.success && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      alert('Failed to open customer portal');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of the current billing period.')) {
      return;
    }

    try {
      await axios.post(`/api/billing/subscription/${tenantId}/cancel`);
      alert('Subscription will be canceled at the end of the billing period');
      fetchBillingData();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      alert('Failed to cancel subscription');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(`/api/billing/subscription/${tenantId}/reactivate`);
      alert('Subscription reactivated successfully');
      fetchBillingData();
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      alert('Failed to reactivate subscription');
    }
  };

  if (loading) {
    return (
      <div className="billing-dashboard">
        <div className="loading">Loading billing information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-dashboard">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="billing-dashboard">
        <div className="no-subscription">
          <h2>No Subscription Found</h2>
          <p>Please set up your billing to continue using On-Screen.</p>
          <button className="btn-primary">Set Up Billing</button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'TRIALING': return '#ff6800';
      case 'PAST_DUE': return '#dc3545';
      case 'CANCELED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getUsagePercentage = (current, limit, isUnlimited) => {
    if (isUnlimited) return 0;
    return Math.min(100, (current / limit) * 100);
  };

  return (
    <div className="billing-dashboard">
      <h1>Billing & Subscription</h1>

      {/* Subscription Status Card */}
      <div className="billing-card">
        <div className="card-header">
          <h2>Current Plan</h2>
          <span 
            className="status-badge" 
            style={{ backgroundColor: getStatusColor(subscription.status) }}
          >
            {subscription.status_display}
          </span>
        </div>
        <div className="card-body">
          <div className="plan-info">
            <div className="plan-name">{subscription.plan_name}</div>
            <div className="plan-price">
              ${subscription.price_monthly}/month
            </div>
          </div>

          {subscription.status === 'TRIALING' && subscription.trial_days_remaining > 0 && (
            <div className="trial-info">
              <strong>{subscription.trial_days_remaining} days</strong> remaining in trial
              <div className="trial-end">Trial ends on {formatDate(subscription.trial_end)}</div>
            </div>
          )}

          {subscription.cancel_at_period_end && (
            <div className="cancellation-notice">
              <strong>Subscription will cancel</strong> on {formatDate(subscription.current_period_end)}
              <button 
                className="btn-secondary" 
                onClick={handleReactivateSubscription}
              >
                Reactivate Subscription
              </button>
            </div>
          )}

          <div className="billing-period">
            <div>Current Period:</div>
            <div>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</div>
          </div>

          <div className="action-buttons">
            <button className="btn-primary" onClick={handleOpenPortal}>
              Manage Billing
            </button>
            {!subscription.cancel_at_period_end && (
              <button className="btn-secondary" onClick={handleCancelSubscription}>
                Cancel Subscription
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Usage Overview Card */}
      {usage && (
        <div className="billing-card">
          <div className="card-header">
            <h2>Current Usage</h2>
            <button className="btn-link" onClick={fetchBillingData}>
              Refresh
            </button>
          </div>
          <div className="card-body">
            {/* Users */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">Users</span>
                <span className="usage-count">
                  {usage.active_users} 
                  {!usage.is_unlimited_users && ` / ${usage.included_users}`}
                  {usage.is_unlimited_users && ' (Unlimited)'}
                </span>
              </div>
              {!usage.is_unlimited_users && (
                <div className="usage-bar">
                  <div 
                    className="usage-fill" 
                    style={{ 
                      width: `${getUsagePercentage(usage.active_users, usage.included_users, usage.is_unlimited_users)}%`,
                      backgroundColor: usage.users_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
              {usage.extra_users_billed > 0 && (
                <div className="extra-charge">
                  +${usage.extra_user_charge.toFixed(2)}/month for {usage.extra_users_billed} extra user(s)
                </div>
              )}
            </div>

            {/* Integrations */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">Integrations</span>
                <span className="usage-count">
                  {usage.active_integrations} 
                  {!usage.is_unlimited_integrations && ` / ${usage.included_integrations}`}
                  {usage.is_unlimited_integrations && ' (Unlimited)'}
                </span>
              </div>
              {!usage.is_unlimited_integrations && (
                <div className="usage-bar">
                  <div 
                    className="usage-fill" 
                    style={{ 
                      width: `${getUsagePercentage(usage.active_integrations, usage.included_integrations, usage.is_unlimited_integrations)}%`,
                      backgroundColor: usage.integrations_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Scheduled Posts */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">Scheduled Posts (this month)</span>
                <span className="usage-count">
                  {usage.scheduled_posts_count} 
                  {!usage.is_unlimited_posts && ` / ${usage.included_posts_monthly}`}
                  {usage.is_unlimited_posts && ' (Unlimited)'}
                </span>
              </div>
              {!usage.is_unlimited_posts && (
                <div className="usage-bar">
                  <div 
                    className="usage-fill" 
                    style={{ 
                      width: `${getUsagePercentage(usage.scheduled_posts_count, usage.included_posts_monthly, usage.is_unlimited_posts)}%`,
                      backgroundColor: usage.posts_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cost Estimate Card */}
      {cost && (
        <div className="billing-card">
          <div className="card-header">
            <h2>Estimated Monthly Cost</h2>
          </div>
          <div className="card-body">
            <div className="cost-breakdown">
              <div className="cost-item">
                <span>Base Plan ({cost.planName})</span>
                <span>${cost.baseCost.toFixed(2)}</span>
              </div>
              {cost.extraUsers > 0 && (
                <div className="cost-item">
                  <span>Extra Users ({cost.extraUsers})</span>
                  <span>${cost.extraUserCost.toFixed(2)}</span>
                </div>
              )}
              <div className="cost-total">
                <span>Total</span>
                <span>${cost.totalCost.toFixed(2)}/{cost.currency.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingDashboard;